/**
 * Module dependencies.
 */

import { Title } from 'app/components/atoms/title/title';
import { GridTable } from 'app/components/organisms/grid-table/grid-table';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { appRoutes } from 'app/routes';
import React, { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styles from './sales-list.module.less';
import { Button } from 'app/components/atoms/button/button';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { useCrudDelete } from 'app/hooks/app-requests/crud/use-crud-delete';
import { CrudTemplate } from 'app/components/organisms/crud/form-types';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { useCrudList } from 'app/hooks/app-requests/crud/use-crud-list';
import { Sale } from 'app/types/sale';

/**
 * Columns.
 */

const createColumns = (translate: TFunction) => [
  {
    key: 'id',
    title: translate('common.table.columns.id'),
    render: (item: Sale) => `${item.id.substring(0, 6)}...`
  },
  {
    key: 'pharmacy.name',
    title: translate('common.table.columns.pharmacyId')
  },
  {
    key: 'externalOrderId',
    title: translate('common.table.columns.externalOrderId')
  },
  {
    key: 'status',
    title: translate('common.table.columns.status')
  },
  {
    key: 'isAnnulled',
    title: translate('common.table.columns.isAnnulled')
  },
  {
    key: 'createdAt',
    title: translate('common.table.columns.createdAt')
  },
  {
    key: 'updatedAt',
    title: translate('common.table.columns.updatedAt')
  },
  {
    key: 'availableItems',
    title: translate('common.table.columns.availableItems'),
    render: (item: Sale) => `${item.lines.filter(it => !it.deletedAt).length} / ${item.lines.length}`
  }
];

/**
 * Export `SalesList` component.
 */

export function SalesList(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const template = useMemo((): CrudTemplate<Sale> => {
    return {
      list: {
        columns: createColumns(translate),
        route: appRoutes.dashboard.users.detail.saleEdit,
        addButton: translate('common.actions.add'),
        canEdit: true,
        canRemove: true,
        resolveParams: (params: any, item?: Sale) => {
          return {
            userId: params.userId,
            id: params.id ?? item?.id
          };
        },
        key: ['userPinNumbers'],
        normalizeData: (result: any) => {
          return result;
        },
        pagination: {
          pageSize: 25,
          pageSizeOptions: [10, 25, 50, 100, 500, 1000]
        },
        endpoint: apiEndpoints.userSales,
        endpointById: apiEndpoints.userSalesById
      },
      remove: {
        endpoint: apiEndpoints.userSalesById
      }
    };
  }, [translate]);

  const { mutate: deleteAction, isLoading } = useCrudDelete(template, params);
  const { data } = useCrudList<Sale>(template, params, {});

  const computedTemplate = useMemo(() => {
    if (!template.list) {
      return template;
    }

    template.list.columns = [
      ...createColumns(translate),
      {
        key: 'actions',
        title: translate('gridTable.labels.actions'),
        render: (item: Sale) => {
          const to = resolveAppPath(appRoutes.dashboard.users.detail.saleEdit, { userId: params.userId, id: item.id });

          return (
            <>
              <Link
                onClick={event => {
                  event.stopPropagation();
                }}
                to={to}
              >
                <Button icon={<EditOutlined />} size={'small'} />
              </Link>

              <Popconfirm
                okButtonProps={{ loading: isLoading }}
                {...{ onClick: (event: any) => event.stopPropagation() }}
                onConfirm={async (event: any) => {
                  event.stopPropagation();
                  event.preventDefault();

                  await deleteAction(item);

                  return;
                }}
                title={translate('removeAction')}
              >
                <Button danger icon={<DeleteOutlined />} size={'small'} />
              </Popconfirm>
            </>
          );
        }
      }
    ];

    return template;
  }, [deleteAction, isLoading, params.userId, template, translate]);

  return (
    <>
      <div className={styles.headerContent}>
        <Title className={styles.title}>{translate('users.sales.title')}</Title>

        <Link to={resolveAppPath(appRoutes.dashboard.users.detail.salesAdd, { userId: params.userId })}>
          <Button type={'primary'}>{translate('common.actions.add')}</Button>
        </Link>
      </div>

      <GridTable
        columns={computedTemplate.list?.columns}
        data={data?.results}
        onClickLine={(item: Sale) => {
          const url = resolveAppPath(appRoutes.dashboard.users.detail.salesDetail ?? '', {
            userId: params.userId,
            id: item.id
          });

          navigate(url);
        }}
      />
    </>
  );
}
